import { InputState } from '../../types/game.types';
import { BaseGame } from '../BaseGame';
import { RenderManager } from '../../core/RenderManager';
import { AudioManager } from '../../core/AudioManager';
import { Player } from './entities/Player';
import { Enemy } from './entities/Enemy';
import { Bullet, BulletType } from './entities/Bullet';
import { EnemyFormation } from './systems/EnemyFormation';
import { CollisionSystem } from './systems/CollisionSystem';

export class GalacticDefenseGame extends BaseGame {
  private player: Player | null = null;
  private bullets: Bullet[] = [];
  private enemyFormation: EnemyFormation;
  private nextBulletId: number = 1;
  
  // Game timing
  private enemyShootTimer: number = 0;
  private enemyShootInterval: number = 1.5; // seconds between enemy shots
  
  // Game state
  private gameStarted: boolean = false;
  private levelComplete: boolean = false;
  private levelTransitionTimer: number = 0;
  private readonly LEVEL_TRANSITION_DELAY = 2; // seconds

  constructor(renderManager: RenderManager, audioManager: AudioManager) {
    const gameConfig = { width: 600, height: 900, fps: 60, scale: 1 }; // Portrait for mobile
    super(renderManager, audioManager, gameConfig, 'galactic-defense');
    this.enemyFormation = new EnemyFormation(renderManager, audioManager, this.config.width);
  }

  public init(): void {
    console.log('Galactic Defense Game initialized');
    
    // Reset game state
    this.score = 0;
    this.lives = 3;
    this.level = 1;
    this.isGameOver = false;
    this.gameStarted = true;
    this.levelComplete = false;
    this.levelTransitionTimer = 0;
    
    // Clear all game objects
    this.bullets = [];
    this.nextBulletId = 1;
    
    // Create player
    this.createPlayer();
    
    // Create enemy formation
    this.enemyFormation.reset();
    this.enemyFormation.createFormation(this.level);
    
    // Reset enemy shoot timer
    this.enemyShootTimer = 0;
  }

  private createPlayer(): void {
    const playerX = this.config.width / 2 - 8; // Center horizontally
    const playerY = this.config.height - 25; // Near bottom with minimal margin

    this.player = new Player(
      'player',
      playerX,
      playerY,
      this.renderManager,
      this.audioManager,
      this.config.width
    );
  }

  public update(deltaTime: number): void {
    if (this.isGameOver) return;
    
    if (this.levelComplete) {
      this.levelTransitionTimer += deltaTime;
      if (this.levelTransitionTimer >= this.LEVEL_TRANSITION_DELAY) {
        this.startNextLevel();
      }
      return;
    }
    
    if (!this.gameStarted || !this.player) return;
    
    // Update player
    if (this.player.active) {
      this.player.update(deltaTime);
    }
    
    // Update enemy formation
    this.enemyFormation.update(deltaTime);
    
    // Update bullets
    this.updateBullets(deltaTime);
    
    // Handle enemy shooting
    this.handleEnemyShooting(deltaTime);
    
    // Check collisions
    this.checkCollisions();
    
    // Check win/lose conditions
    this.checkWinLoseConditions();
  }

  private updateBullets(deltaTime: number): void {
    // Update all bullets
    this.bullets.forEach(bullet => bullet.update(deltaTime));
    
    // Remove inactive bullets
    this.bullets = this.bullets.filter(bullet => bullet.active);
  }

  private handleEnemyShooting(deltaTime: number): void {
    this.enemyShootTimer += deltaTime;
    
    if (this.enemyShootTimer >= this.enemyShootInterval) {
      // Authentic Space Invaders: enemies only shoot after dropping 3 times
      if (!this.enemyFormation.canShootMissiles()) {
        console.log('Enemies not ready to shoot missiles yet');
        this.enemyShootTimer = 0;
        return;
      }
      
      console.log('Enemies can shoot - attempting to create missile');
      
      // Authentic Space Invaders: maximum 3 enemy missiles on screen at any time
      const enemyBulletsOnScreen = this.bullets.filter(bullet => 
        bullet.active && bullet.getType() === BulletType.ENEMY
      ).length;
      
      if (enemyBulletsOnScreen < 3) {
        // Get bottom row enemies that can shoot
        const shooters = this.enemyFormation.getBottomRowEnemies();
        
        if (shooters.length > 0) {
          // Pick a random shooter
          const shooter = shooters[Math.floor(Math.random() * shooters.length)];
          
          if (shooter.shouldShoot()) {
            const shootPos = shooter.getShootPosition();
            this.createEnemyBullet(shootPos.x, shootPos.y);
            console.log(`Enemy missile created at (${shootPos.x}, ${shootPos.y})`);
          }
        }
      }
      
      this.enemyShootTimer = 0;
    }
  }

  private createPlayerBullet(x: number, y: number): void {
    const bullet = new Bullet(
      `bullet_player_${this.nextBulletId++}`,
      x,
      y,
      BulletType.PLAYER,
      this.renderManager
    );
    this.bullets.push(bullet);
  }

  private createEnemyBullet(x: number, y: number): void {
    // Enemy bullet speed increases aggressively with level
    // Base speed: 180, increases by 35 per level, max 600
    const enemyBulletSpeed = Math.min(600, 180 + (this.level - 1) * 35);
    
    const bullet = new Bullet(
      `bullet_enemy_${this.nextBulletId++}`,
      x,
      y,
      BulletType.ENEMY,
      this.renderManager,
      enemyBulletSpeed
    );
    this.bullets.push(bullet);
  }

  private checkCollisions(): void {
    if (!this.player) return;
    
    const enemies = this.enemyFormation.getActiveEnemies();
    
    // Bullet vs Enemy collisions
    const bulletEnemyCollisions = CollisionSystem.checkBulletEnemyCollisions(this.bullets, enemies);
    bulletEnemyCollisions.forEach(({ bullet, enemy }) => {
      bullet.destroy();
      enemy.destroy();
      this.addScore(enemy.getPointValue());
      
      // Play explosion sound
      this.audioManager.playExplosion();
    });
    
    // Bullet vs Player collisions
    const bulletPlayerCollisions = CollisionSystem.checkBulletPlayerCollisions(this.bullets, this.player);
    bulletPlayerCollisions.forEach(bullet => {
      bullet.destroy();
      if (this.player && this.player.active) {
        this.playerHit();
      }
    });
    
    // Enemy vs Player collisions (direct contact)
    const enemyPlayerCollisions = CollisionSystem.checkEnemyPlayerCollisions(enemies, this.player);
    if (enemyPlayerCollisions.length > 0 && this.player.active) {
      this.playerHit();
    }
    
    // Bullet vs Bullet collisions - Authentic Space Invaders rules
    const bulletBulletCollisions = CollisionSystem.checkBulletBulletCollisions(this.bullets);
    bulletBulletCollisions.forEach(({ bullet1, bullet2 }) => {
      // Player's laser is always destroyed
      // Enemy missile occasionally survives (30% survival rate, higher for "wiggly" missiles)
      const playerBullet = bullet1.getType() === BulletType.PLAYER ? bullet1 : bullet2;
      const enemyBullet = bullet1.getType() === BulletType.ENEMY ? bullet1 : bullet2;
      
      // Player bullet always destroyed
      playerBullet.destroy();
      
      // Enemy missile survival chance (simulate "wiggly" missiles being more resilient)
      const survivalChance = 0.3; // 30% chance missile survives
      if (Math.random() > survivalChance) {
        enemyBullet.destroy();
      }
    });
  }

  private playerHit(): void {
    if (!this.player) return;
    
    this.player.destroy();
    this.loseLife();
    
    if (!this.isGameOver) {
      // Respawn player after a delay
      setTimeout(() => {
        if (!this.isGameOver) {
          this.createPlayer();
        }
      }, 1000);
    }
  }

  private checkWinLoseConditions(): void {
    if (!this.player) return;
    
    // Check if all enemies are destroyed
    if (this.enemyFormation.isEmpty() && !this.levelComplete) {
      this.levelComplete = true;
      this.levelTransitionTimer = 0;
      this.audioManager.playPowerUp(); // Victory sound
    }
    
    // Check if enemies reached the invasion line
    // Authentic Space Invaders: invasion occurs 11 drops from screen 1 starting position
    const baseY = 60; // Screen 1 starting position
    const dropDistance = 25 + Math.floor(this.level / 2) * 8; // Current drop distance for this level
    const invasionY = baseY + (11 * dropDistance); // 11 drops from screen 1
    
    if (this.enemyFormation.hasReachedBottom(invasionY)) {
      this.gameOver();
    }
  }

  private startNextLevel(): void {
    this.nextLevel();
    this.levelComplete = false;
    this.levelTransitionTimer = 0;
    
    // Clear bullets
    this.bullets = [];
    
    // Create new enemy formation with increased difficulty
    this.enemyFormation.reset();
    this.enemyFormation.createFormation(this.level);
    
    // Give player a new life every 5 levels as a reward (reduced frequency)
    if (this.level % 5 === 0) {
      this.addLife();
      console.log(`Level ${this.level}: Bonus life! Total lives: ${this.lives}`);
    }
    
    // Aggressive difficulty adjustments
    // Enemy shooting becomes MUCH more frequent (min 0.15 seconds between shots)
    this.enemyShootInterval = Math.max(0.15, 1.2 - (this.level - 1) * 0.2);
    
    console.log(`Level ${this.level} started - Enemy shoot interval: ${this.enemyShootInterval.toFixed(2)}s`);
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Clear screen with space background
    this.renderManager.clear('#000000');
    
    // Draw stars background
    this.drawStarfield();
    
    // Render player
    if (this.player && this.player.active) {
      this.player.render(ctx);
    }
    
    // Render enemies
    this.enemyFormation.getAllEnemies().forEach(enemy => {
      if (enemy.active) {
        enemy.render(ctx);
      }
    });
    
    // Render bullets
    this.bullets.forEach(bullet => {
      if (bullet.active) {
        bullet.render(ctx);
      }
    });
    
    // Render HUD
    this.renderGameHUD();
    
    // Render level transition screen
    if (this.levelComplete) {
      this.renderLevelComplete();
    }
    
    // Render game over screen
    if (this.isGameOver) {
      this.renderGameOver();
    }
    
    // Render initial entry if showing
    if (this.showingInitialEntry && this.initialEntry) {
      console.log('GalacticDefenseGame: Rendering initial entry component');
      this.initialEntry.render(ctx, this.config.width / 2, this.config.height / 2);
    }
  }

  private drawStarfield(): void {
    // Simple animated starfield
    const time = Date.now() * 0.001;
    
    for (let i = 0; i < 50; i++) {
      const x = (i * 137 + time * 20) % this.config.width;
      const y = (i * 157 + time * 15) % this.config.height;
      const brightness = Math.sin(time + i) * 0.5 + 0.5;
      const alpha = Math.floor(brightness * 255);
      
      this.renderManager.drawPixel(x, y, `rgba(255, 255, 255, ${alpha / 255})`);
    }
  }

  private renderGameHUD(): void {
    // Score
    this.renderManager.drawText(
      `SCORE: ${this.score.toString().padStart(6, '0')}`, 
      20, 
      20, 
      '#FFFFFF', 
      '16px "Courier New", monospace'
    );
    
    // Lives
    this.renderManager.drawText('LIVES: ', this.config.width - 150, 20, '#FFFFFF', '16px "Courier New", monospace');
    for (let i = 0; i < this.lives; i++) {
      this.renderManager.drawC64Char('▲', this.config.width - 80 + i * 20, 18, '#62D0FE', 1);
    }
    
    // Level with difficulty indicator
    const difficultyColor = this.level <= 3 ? '#55A049' : // Green for easy levels
                           this.level <= 6 ? '#BFCE72' : // Yellow for medium
                           this.level <= 9 ? '#FFA500' : // Orange for hard
                           '#883932'; // Red for extreme
    
    this.renderManager.drawTextCentered(
      `LEVEL ${this.level}`, 
      this.config.width / 2, 
      20, 
      difficultyColor, 
      '18px "Courier New", monospace'
    );
    
    // Show difficulty stars
    const difficultyStars = Math.min(5, Math.ceil(this.level / 2));
    const starString = '★'.repeat(difficultyStars) + '☆'.repeat(5 - difficultyStars);
    this.renderManager.drawTextCentered(
      starString,
      this.config.width / 2,
      38,
      difficultyColor,
      '12px "Courier New", monospace'
    );
    
    // Enemy count
    const enemiesLeft = this.enemyFormation.getActiveEnemies().length;
    this.renderManager.drawText(
      `ENEMIES: ${enemiesLeft}`, 
      20, 
      this.config.height - 30, 
      '#787878', 
      '12px "Courier New", monospace'
    );
  }

  private renderLevelComplete(): void {
    // Semi-transparent overlay
    this.renderManager.fillRect(0, 0, this.config.width, this.config.height, 'rgba(0, 0, 0, 0.7)');
    
    const centerX = this.config.width / 2;
    const centerY = this.config.height / 2;
    
    this.renderManager.drawTextCentered(
      'LEVEL COMPLETE!', 
      centerX, 
      centerY - 40, 
      '#55A049', 
      '32px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      `LEVEL ${this.level} CLEARED`, 
      centerX, 
      centerY, 
      '#BFCE72', 
      '18px "Courier New", monospace'
    );
    
    this.renderManager.drawTextCentered(
      'PREPARING NEXT WAVE...', 
      centerX, 
      centerY + 40, 
      '#FFFFFF', 
      '14px "Courier New", monospace'
    );
  }

  public handleInput(input: InputState): void {
    if (this.isGameOver) {
      // Don't handle input if showing initial entry
      if (this.showingInitialEntry) {
        return;
      }
      
      // Handle game over input
      if (input.keys['Space'] || input.touch.gesture === 'tap') {
        this.restart();
      }
      return;
    }
    
    if (!this.player || !this.player.active) return;
    
    // Player movement
    if (input.keys['ArrowLeft'] || input.keys['KeyA']) {
      this.player.moveLeft();
    } else if (input.keys['ArrowRight'] || input.keys['KeyD']) {
      this.player.moveRight();
    } else {
      this.player.stopMoving();
    }
    
    // Player shooting - allow multiple shots for faster gameplay (max 3 on screen)
    if (input.keys['Space'] || input.touch.gesture === 'tap') {
      const playerBulletsOnScreen = this.bullets.filter(bullet => 
        bullet.active && bullet.getType() === BulletType.PLAYER
      ).length;
      
      if (playerBulletsOnScreen < 3) {
        const bulletPos = this.player.fireBullet();
        if (bulletPos) {
          this.createPlayerBullet(bulletPos.x, bulletPos.y);
        }
      }
    }
    
    // Touch controls for mobile
    if (input.touch.active && this.player) {
      const touchX = input.touch.position.x;
      const playerCenterX = this.player.position.x + this.player.size.x / 2;
      
      if (Math.abs(touchX - playerCenterX) > 20) {
        if (touchX < playerCenterX) {
          this.player.moveLeft();
        } else {
          this.player.moveRight();
        }
      } else {
        this.player.stopMoving();
      }
    }
  }

  public cleanup(): void {
    super.cleanup();
    this.bullets = [];
    this.player = null;
    this.enemyFormation.reset();
  }
}